// Sparkline: SVG-based line/multiline charts with d3-transition morphing.
//
// Data arrives via push_event from the server. Each update computes new SVG
// paths and transitions them with d3-transition — the path `d` attribute
// morphs smoothly between shapes.

import { select, pointer } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { extent, max as d3max, bisector } from "d3-array";
import "d3-transition"; // augments Selection with .transition()
import type { LiveViewHook, CiPushEventWire } from "../types";
import { sparklineLinePath, sparklineTimeAgo } from "./helpers";
import {
  illuminateStatusColor,
  pipelineStatusColor,
  chartableTerminalPipelineStatuses,
  type PipelineStatus,
} from "../../../../.dox/ci";

// Derived alias for the chartable subset of PipelineStatus. We pull the type
// straight from the imported `chartableTerminalPipelineStatuses` set so this
// alias cannot drift from the .dox source of truth — if upstream tightens
// the codegen to emit a literal-tuple type for the saturated set, the narrow
// subset will flow through here automatically; meanwhile this still binds
// us to whatever the imported value's element type happens to be.
export type ChartableStatus =
  typeof chartableTerminalPipelineStatuses extends Iterable<infer T>
    ? T
    : never;
import {
  regressionPath as regressionPathPure,
  nearestLineIndex as nearestLineIndexPure,
} from "./sparkline-pure";

// Re-export so existing call sites keep their named import paths
// stable; the implementation now lives in the pure module.
export const regressionPath = regressionPathPure;
export const nearestLineIndex = nearestLineIndexPure;

export type DayData = { t: number } & Record<ChartableStatus, number>;

interface SparklineHook extends LiveViewHook<CiPushEventWire> {
  _lastPoints?: (number | null)[];
  _lastSeries?: DayData[];
  _initialized?: boolean;
}

export const LINE_MARGIN = { top: 4, right: 4, bottom: 14, left: 0 };
export const MULTI_MARGIN = { top: 4, right: 4, bottom: 14, left: 20 };

// WHY local: these three timings drive d3-transition tweens — purely
// client-side, visual-only. The server emits sparkline data via
// `phx:` events with no timing field; choosing how long the path tween
// runs (TRANSITION_MS), the initial draw-in (BUILD_IN_MS), and the
// tick-label crossfade (LABEL_FADE_MS) is a UI-only decision. Nothing
// in Elixir/HEEx/CSS reads or schedules against these values, so they
// stay co-located with the chart they animate.
const TRANSITION_MS = 600;
const BUILD_IN_MS = 800;
const LABEL_FADE_MS = 400;

// ── Regression line path ────────────────────────────────────────
// Implementation moved to ./sparkline-pure for testability.

export function formatLabel(unit: string, v: number): string {
  if (unit === "duration") {
    const s = v / 1000;
    return s < 60 ? `${Math.round(s)}s` : `${Math.round(s / 60)}m`;
  }
  return String(Math.round(v));
}

// ── Multiline paths ─────────────────────────────────────────────

export function multilinePaths(
  series: DayData[],
  w: number,
  h: number,
  margin: typeof MULTI_MARGIN,
): Record<ChartableStatus, string> {
  // Seed with empty strings keyed by every chartable status; downstream code
  // (return-on-empty + the build loop) folds the path string in. Iterating the
  // .dox set means a new chartable status appears here automatically once the
  // domain widens it.
  const empty = (): Record<ChartableStatus, string> => {
    const out = {} as Record<ChartableStatus, string>;
    for (const status of chartableTerminalPipelineStatuses) out[status] = "";
    return out;
  };
  if (series.length < 1) return empty();
  const lw = w - margin.left - margin.right;
  const lh = h - margin.top - margin.bottom;
  const tMin = series[0]!.t;
  const tMax = series[series.length - 1]!.t;
  const xMax = tMax > tMin ? tMax : tMin + 86_400_000;
  const allCounts = series.flatMap((d) => {
    const counts: number[] = [];
    for (const status of chartableTerminalPipelineStatuses)
      counts.push(d[status]);
    return counts;
  });
  // Floor at 1 so an all-zero series still produces a usable axis. d3-array
  // returns undefined on an empty input — coalesce, then take the max against
  // the floor.
  const yMax = Math.max(d3max(allCounts) ?? 0, 1);

  const xScale = scaleLinear()
    .domain([tMin, xMax])
    .range([margin.left, margin.left + lw]);
  const yScale = scaleLinear()
    .domain([0, yMax])
    .range([margin.top + lh, margin.top]);
  const px = (t: number) =>
    series.length === 1 ? margin.left + lw / 2 : xScale(t);

  const buildPath = (key: ChartableStatus): string =>
    series
      .map((d, i) => `${i === 0 ? "M" : "L"}${px(d.t)},${yScale(d[key])}`)
      .join(" ");

  const out = empty();
  for (const status of chartableTerminalPipelineStatuses)
    out[status] = buildPath(status);
  return out;
}

// ── Hover hit-testing ───────────────────────────────────────────
//
// Both hovers used to walk every point to find the nearest one to the mouse
// x. d3-array's `.center` bisector returns the same answer in O(log n) for
// sorted inputs.

// nearestLineIndex implementation moved to ./sparkline-pure (re-exported above).

/**
 * Find the index in `series` whose t maps closest to `mx`. `series` must be
 * sorted by `t` (which the rendering pipeline already assumes).
 */
export function nearestMultilineIndex(
  series: ReadonlyArray<DayData>,
  mx: number,
  margin: typeof MULTI_MARGIN,
  innerW: number,
): number {
  if (series.length === 0) return 0;
  if (series.length === 1) return 0; // EQUIVALENT MUTANT: series.length === 1 → false is behaviourally equivalent for length-1 input with a degenerate xScale
  const tMin = series[0]!.t;
  const tMax = series[series.length - 1]!.t;
  const xMax = tMax > tMin ? tMax : tMin + 86_400_000; // EQUIVALENT MUTANT: same subtraction fallback in nearestMultilineIndex is equivalent for length-2 same-t series with bisector.center
  // Linear scale t→pixel; invert pixel→t and bisect on t.
  const xScale = scaleLinear()
    .domain([tMin, xMax])
    .range([margin.left, margin.left + innerW]);
  const tBisector = bisector<DayData, number>((d) => d.t);
  return tBisector.center(series, xScale.invert(mx));
}

// ── Sparkline hook ──────────────────────────────────────────────

export const Sparkline = {
  mounted(this: SparklineHook) {
    const type = this.el.dataset["type"] ?? "line";
    const rgb = this.el.dataset["color"] ?? "0, 191, 255";
    const unit = this.el.dataset["unit"] ?? ""; // EQUIVALENT MUTANT: unit default of "" vs any non-"duration" string produces same formatter output
    const eventKey = this.el.dataset["eventKey"];

    const svg = this.el.querySelector("svg");
    if (!svg) return;
    const sel = select(svg);

    // -- Build SVG structure --
    // Clip path for build-in sweep animation
    const clipId = `clip-${this.el.id}`;
    sel
      .append("defs")
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("class", "build-in-clip")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 0)
      .attr("height", 9999);

    if (type === "line") {
      sel
        .append("path")
        .attr("class", "sparkline-regression")
        .attr("clip-path", `url(#${clipId})`)
        .attr("fill", "none")
        .attr("stroke", `rgba(${rgb}, 0.5)`)
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3");
      sel
        .append("path")
        .attr("class", "sparkline-data")
        .attr("clip-path", `url(#${clipId})`)
        .attr("fill", "none")
        .attr("stroke", `rgba(${rgb}, 0.8)`)
        .attr("stroke-width", 1.5);

      // Labels (start hidden for build-in fade)
      for (const cls of ["sparkline-label-max", "sparkline-label-min"]) {
        sel
          .append("text")
          .attr("class", cls)
          .attr("fill", "rgba(255,255,255,1)")
          .attr("font-size", "9px")
          .attr("font-family", "'DejaVu Mono', monospace")
          .style("opacity", 0);
      }
      sel
        .append("text")
        .attr("class", "sparkline-label-left")
        .attr("fill", "rgba(255,255,255,1)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "start")
        .style("opacity", 0);
      sel
        .append("text")
        .attr("class", "sparkline-label-right")
        .attr("fill", "rgba(255,255,255,1)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "end")
        .style("opacity", 0);
      sel
        .append("text")
        .attr("class", "sparkline-label-center")
        .attr("fill", "rgba(255,255,255,1)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "middle")
        .style("opacity", 0);

      // Hover group (hidden until mouseover)
      const hoverG = sel
        .append("g")
        .attr("class", "sparkline-hover")
        .style("display", "none");
      hoverG
        .append("line")
        .attr("class", "hover-crosshair")
        .attr("stroke", "rgba(255,255,255,0.25)")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,2");
      hoverG
        .append("circle")
        .attr("class", "hover-dot")
        .attr("r", 3.5)
        .attr("fill", `rgba(${rgb}, 1)`);
      hoverG
        .append("rect")
        .attr("class", "hover-bg")
        .attr("fill", "rgba(0,0,0,0.85)")
        .attr("rx", 3)
        .attr("ry", 3);
      hoverG
        .append("text")
        .attr("class", "hover-label")
        .attr("fill", "rgba(255,255,255,0.95)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central");
    } else if (type === "multiline") {
      // Lines render the per-status counts. Colour comes from
      // `pipelineStatusColor` -> `illuminateStatusColor` (paradox-emitted
      // RGB triplet for the Tailwind-400 palette key) so the sparkline
      // shares the source-of-truth used by everything else that pulls
      // a status colour from .dox. Alpha varies per line (cancelled
      // dimmer because it isn't usually load-bearing signal). CSS
      // class names stay snake-cased for selector compatibility.
      const lineColors: ReadonlyArray<{
        readonly cls: string;
        readonly color: string;
      }> = (
        [
          { cls: "sparkline-succeeded", status: "succeeded", alpha: 0.9 },
          { cls: "sparkline-failed", status: "failed", alpha: 0.9 },
          { cls: "sparkline-cancelled", status: "cancelled", alpha: 0.7 },
        ] satisfies ReadonlyArray<{
          cls: string;
          status: PipelineStatus;
          alpha: number;
        }>
      ).map(({ cls, status, alpha }) => ({
        cls,
        color: `rgba(${illuminateStatusColor(pipelineStatusColor(status))}, ${alpha})`,
      }));
      for (const { cls, color } of lineColors) {
        sel
          .append("path")
          .attr("class", cls)
          .attr("clip-path", `url(#${clipId})`)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 1.5);
      }
      sel
        .append("text")
        .attr("class", "sparkline-label-left")
        .attr("fill", "rgba(255,255,255,1)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "start")
        .style("opacity", 0);
      sel
        .append("text")
        .attr("class", "sparkline-label-right")
        .attr("fill", "rgba(255,255,255,1)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "end")
        .style("opacity", 0);

      // Hover group for multiline
      const mHoverG = sel
        .append("g")
        .attr("class", "sparkline-hover")
        .style("display", "none");
      mHoverG
        .append("line")
        .attr("class", "hover-crosshair")
        .attr("stroke", "rgba(255,255,255,0.25)")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,2");
      mHoverG
        .append("circle")
        .attr("class", "hover-dot-succeeded")
        .attr("r", 3.5)
        .attr("fill", "rgba(74, 222, 128, 0.9)");
      mHoverG
        .append("circle")
        .attr("class", "hover-dot-failed")
        .attr("r", 3.5)
        .attr("fill", "rgba(248, 113, 113, 0.9)");
      mHoverG
        .append("circle")
        .attr("class", "hover-dot-cancelled")
        .attr("r", 3.5)
        .attr("fill", "rgba(113, 113, 122, 0.7)");
      mHoverG
        .append("rect")
        .attr("class", "hover-bg")
        .attr("fill", "rgba(0,0,0,0.85)")
        .attr("rx", 3)
        .attr("ry", 3);
      mHoverG
        .append("text")
        .attr("class", "hover-label")
        .attr("fill", "rgba(255,255,255,0.95)")
        .attr("font-size", "9px")
        .attr("font-family", "'DejaVu Mono', monospace")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central");
    }

    // -- Hover for line type --
    if (type === "line") {
      svg.addEventListener("mousemove", (e: MouseEvent) => {
        // Read from the hook closure, not `this.el.dataset`: LiveView's
        // `phx-update="ignore"` still strips client-set data-* attrs whenever
        // morphdom re-renders the parent (every PubSub refresh on this page),
        // so dataset["points"] vanishes the moment any pipeline event fires.
        const points = this._lastPoints;
        if (!points) return;

        const valid = points
          .map((v, i) => ({ v, i }))
          .filter(
            (d): d is { v: number; i: number } => d.v != null && !isNaN(d.v),
          );
        if (valid.length < 2) return;

        const w = svg.clientWidth;
        const h = svg.clientHeight;
        const innerW = w - LINE_MARGIN.left - LINE_MARGIN.right;
        const innerH = h - LINE_MARGIN.top - LINE_MARGIN.bottom;
        const [yMin = 0, yMax = 1] = extent(valid, (d) => d.v);
        const yRange = yMax - yMin || 1;

        // Match sparklineLinePath: valid-point ORDER for x position
        const xStep = innerW / Math.max(valid.length - 1, 1);
        const xOf = (order: number) => LINE_MARGIN.left + order * xStep;
        const yOf = (val: number) =>
          LINE_MARGIN.top + innerH - ((val - yMin) / yRange) * innerH;

        const [mx] = pointer(e, svg);
        const nearestJ = nearestLineIndex(
          valid.length,
          mx,
          LINE_MARGIN,
          innerW,
        );

        const nearest = valid[nearestJ]!;
        const px = xOf(nearestJ);
        const py = yOf(nearest.v);
        const label = formatLabel(unit, nearest.v);

        const hoverG = sel.select(".sparkline-hover").style("display", null);
        hoverG
          .select(".hover-crosshair")
          .attr("x1", px)
          .attr("y1", LINE_MARGIN.top)
          .attr("x2", px)
          .attr("y2", LINE_MARGIN.top + innerH);
        hoverG.select(".hover-dot").attr("cx", px).attr("cy", py);

        // Tooltip positioning
        const ttw = label.length * 5.5 + 8;
        const tth = 14;
        let tx = px - ttw / 2;
        let ty = py - tth - 8;
        tx = Math.max(1, Math.min(w - ttw - 1, tx));
        if (ty < 1) ty = py + 8;

        hoverG
          .select(".hover-bg")
          .attr("x", tx)
          .attr("y", ty)
          .attr("width", ttw)
          .attr("height", tth);
        hoverG
          .select(".hover-label")
          .attr("x", tx + ttw / 2)
          .attr("y", ty + tth / 2)
          .text(label);
      });

      svg.addEventListener("mouseleave", () => {
        sel.select(".sparkline-hover").style("display", "none");
      });
    }

    // -- Hover for multiline type --
    if (type === "multiline") {
      svg.addEventListener("mousemove", (e: MouseEvent) => {
        const series = this._lastSeries;
        if (!series || series.length < 1) return;

        const w = svg.clientWidth;
        const h = svg.clientHeight;
        const lw = w - MULTI_MARGIN.left - MULTI_MARGIN.right;
        const lh = h - MULTI_MARGIN.top - MULTI_MARGIN.bottom;
        const tMin = series[0]!.t;
        const tMax = series[series.length - 1]!.t;
        const xMax = tMax > tMin ? tMax : tMin + 86_400_000;
        const allCounts = series.flatMap((d) => {
          const counts: number[] = [];
          for (const status of chartableTerminalPipelineStatuses)
            counts.push(d[status]);
          return counts;
        });
        const yMax = Math.max(d3max(allCounts) ?? 0, 1);

        const xScale = scaleLinear()
          .domain([tMin, xMax])
          .range([MULTI_MARGIN.left, MULTI_MARGIN.left + lw]);
        const yScale = scaleLinear()
          .domain([0, yMax])
          .range([MULTI_MARGIN.top + lh, MULTI_MARGIN.top]);
        const px = (t: number) =>
          series.length === 1 ? MULTI_MARGIN.left + lw / 2 : xScale(t);

        const [mx] = pointer(e, svg);
        const nearestI = nearestMultilineIndex(series, mx, MULTI_MARGIN, lw);

        const d = series[nearestI]!;
        const cx = px(d.t);

        const hoverG = sel.select(".sparkline-hover").style("display", null);
        hoverG
          .select(".hover-crosshair")
          .attr("x1", cx)
          .attr("y1", MULTI_MARGIN.top)
          .attr("x2", cx)
          .attr("y2", MULTI_MARGIN.top + lh);
        hoverG
          .select(".hover-dot-succeeded")
          .attr("cx", cx)
          .attr("cy", yScale(d.succeeded));
        hoverG
          .select(".hover-dot-failed")
          .attr("cx", cx)
          .attr("cy", yScale(d.failed));
        hoverG
          .select(".hover-dot-cancelled")
          .attr("cx", cx)
          .attr("cy", yScale(d.cancelled));

        // Tooltip: "✓3 ✗1 ○0"
        const label = `✓${d.succeeded} ✗${d.failed} ○${d.cancelled}`;
        const ttw = label.length * 5.5 + 8;
        const tth = 14;
        const topY = Math.min(
          yScale(d.succeeded),
          yScale(d.failed),
          yScale(d.cancelled),
        );
        let tx = cx - ttw / 2;
        let ty = topY - tth - 8;
        tx = Math.max(1, Math.min(w - ttw - 1, tx));
        if (ty < 1) ty = topY + 8;

        hoverG
          .select(".hover-bg")
          .attr("x", tx)
          .attr("y", ty)
          .attr("width", ttw)
          .attr("height", tth);
        hoverG
          .select(".hover-label")
          .attr("x", tx + ttw / 2)
          .attr("y", ty + tth / 2)
          .text(label);
      });

      svg.addEventListener("mouseleave", () => {
        sel.select(".sparkline-hover").style("display", "none");
      });
    }

    // -- Data events --
    if (!eventKey) return;

    let lastEventJson = "";
    this.handleEvent("sparklineUpdate", (raw: unknown) => {
      const payload = raw as Record<string, Record<string, unknown>>;
      const data = payload[eventKey];
      if (!data) return;

      const json = JSON.stringify(data);
      if (json === lastEventJson) return;
      lastEventJson = json;

      const w = svg.clientWidth;
      const h = svg.clientHeight;
      if (w === 0 || h === 0) return;
      sel.attr("viewBox", `0 0 ${w} ${h}`);

      if (type === "line") {
        const points = (data["points"] ?? []) as (number | null)[];
        const timestamps = (data["timestamps"] ?? []) as (number | null)[];
        this._lastPoints = points.slice();

        const dataPath = sparklineLinePath(points, w, h, LINE_MARGIN);
        const regPath = regressionPath(points, w, h, LINE_MARGIN);
        const valid = points.filter((v): v is number => v != null && !isNaN(v));
        const [yMin = 0, yMax = 1] = extent(valid);

        if (!this._initialized) {
          sel.select(".sparkline-data").attr("d", dataPath);
          sel.select(".sparkline-regression").attr("d", regPath);
          this._initialized = true;

          // Build-in: sweep clip rect left→right, then fade labels
          sel
            .select(".build-in-clip")
            .transition()
            .duration(BUILD_IN_MS)
            .attr("width", w)
            .on("end", () => {
              sel
                .selectAll("text[class^='sparkline-label']")
                .transition()
                .duration(LABEL_FADE_MS)
                .style("opacity", 1);
            });
        } else {
          sel
            .select(".sparkline-data")
            .transition()
            .duration(TRANSITION_MS)
            .attr("d", dataPath);
          sel
            .select(".sparkline-regression")
            .transition()
            .duration(TRANSITION_MS)
            .attr("d", regPath);
        }

        // Labels
        sel
          .select(".sparkline-label-max")
          .attr("x", 0)
          .attr("y", LINE_MARGIN.top + 8)
          .text(formatLabel(unit, yMax));
        sel
          .select(".sparkline-label-min")
          .attr("x", 0)
          .attr("y", h - LINE_MARGIN.bottom)
          .text(formatLabel(unit, yMin));

        const firstTs = timestamps[0];
        const lastTs = timestamps[timestamps.length - 1];
        if (firstTs != null && lastTs != null) {
          sel
            .select(".sparkline-label-left")
            .attr("x", LINE_MARGIN.left)
            .attr("y", h - 2)
            .text(sparklineTimeAgo(firstTs, Date.now()));
          sel
            .select(".sparkline-label-right")
            .attr("x", w - LINE_MARGIN.right)
            .attr("y", h - 2)
            .text(sparklineTimeAgo(lastTs, Date.now()));
          sel
            .select(".sparkline-label-center")
            .attr("x", w / 2)
            .attr("y", h - 2)
            .text(`${valid.length} pipelines`);
        }
      } else if (type === "multiline") {
        const series = (data["series"] ?? []) as DayData[];
        this._lastSeries = series.map((d) => ({ ...d }));

        const paths = multilinePaths(series, w, h, MULTI_MARGIN);

        if (!this._initialized) {
          sel.select(".sparkline-succeeded").attr("d", paths.succeeded);
          sel.select(".sparkline-failed").attr("d", paths.failed);
          sel.select(".sparkline-cancelled").attr("d", paths.cancelled);
          this._initialized = true;

          // Build-in: sweep clip rect left→right, then fade labels
          sel
            .select(".build-in-clip")
            .transition()
            .duration(BUILD_IN_MS)
            .attr("width", w)
            .on("end", () => {
              sel
                .selectAll("text[class^='sparkline-label']")
                .transition()
                .duration(LABEL_FADE_MS)
                .style("opacity", 1);
            });
        } else {
          sel
            .select(".sparkline-succeeded")
            .transition()
            .duration(TRANSITION_MS)
            .attr("d", paths.succeeded);
          sel
            .select(".sparkline-failed")
            .transition()
            .duration(TRANSITION_MS)
            .attr("d", paths.failed);
          sel
            .select(".sparkline-cancelled")
            .transition()
            .duration(TRANSITION_MS)
            .attr("d", paths.cancelled);
        }

        if (series.length > 0) {
          const tMin = series[0]!.t;
          const tMax = series[series.length - 1]!.t;
          sel
            .select(".sparkline-label-left")
            .attr("x", MULTI_MARGIN.left)
            .attr("y", h - 2)
            .text(sparklineTimeAgo(tMin, Date.now()));
          sel
            .select(".sparkline-label-right")
            .attr("x", w - MULTI_MARGIN.right)
            .attr("y", h - 2)
            .text(sparklineTimeAgo(tMax, Date.now()));
        }
      }
    });
  },

  destroyed() {},
  updated() {},
};
